// Shared add bar — Board tab + standup register (Screen 4).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, InputBase, MenuItem, Select } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../design/brainTheme";
import { Icon } from "./icons";
import {
  createProjectAction,
  listJiraAssignableUsers,
  listJiraBoards,
  projectJiraCreate,
  type JiraAssignableUser,
  type JiraBoardRef,
  type ProjectAction,
} from "../lib/brain-api";

const c = brainTokens.color;

export type WorkKind = "jira" | "note" | "question";

export type AddWorkCreated =
  | {
      kind: "jira";
      key: string;
      url: string;
      status?: string;
      summary?: string;
      assignee?: string | null;
      priority?: string | null;
    }
  | { kind: "note" | "question"; id: string; action: ProjectAction };

const ALL_TYPES: WorkKind[] = ["jira", "note", "question"];

const TYPE_LABEL: Record<WorkKind, string> = {
  jira: "Jira issue",
  note: "Note",
  question: "Question",
};

const PRI_OPTIONS = ["P1", "P2", "P3"] as const;
const PRI_TO_JIRA: Record<(typeof PRI_OPTIONS)[number], string> = {
  P1: "Highest",
  P2: "High",
  P3: "Medium",
};

function boardLabel(b: JiraBoardRef): string {
  return b.name ? `${b.name} (${b.key})` : b.key;
}

const fieldSelectSx = {
  fontSize: 12.5,
  color: c.ink3,
  fontFamily: brainTokens.font.sans,
  "&:before, &:after": { display: "none" },
  "& .MuiSelect-select": {
    py: "4px",
    px: "4px",
    borderRadius: "6px",
    border: `1px solid transparent`,
    background: "transparent",
    pr: "22px !important",
  },
  "&:hover .MuiSelect-select, &.Mui-focused .MuiSelect-select": {
    borderColor: c.border,
    background: c.panelBg2,
  },
} as const;

function InlineField({
  label,
  children,
  morphingOut = false,
}: {
  label: string;
  children: React.ReactNode;
  morphingOut?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        flex: "none",
        whiteSpace: "nowrap",
        overflow: "hidden",
        maxWidth: morphingOut ? 0 : 280,
        opacity: morphingOut ? 0 : 1,
        transition: "max-width 120ms ease, opacity 120ms ease",
        pointerEvents: morphingOut ? "none" : "auto",
      }}
    >
      <Box sx={{ fontSize: 11, color: c.faint }}>{label}</Box>
      {children}
    </Box>
  );
}

export function AddWorkBar(props: {
  scope: "board" | "register";
  projectId?: string;
  defaultBoard?: string;
  projects?: { id: string; name: string; board?: string }[];
  types?: Array<"jira" | "note" | "question">;
  onCreated: (r: AddWorkCreated) => void;
}): JSX.Element {
  const { scope, projectId: boardProjectId, defaultBoard, projects, onCreated } = props;
  const enabled = props.types?.length ? props.types : ALL_TYPES;
  const { enqueueSnackbar } = useSnackbar();

  const inputRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<WorkKind>(() => enabled[0] ?? "jira");
  const [text, setText] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [priority, setPriority] = useState<(typeof PRI_OPTIONS)[number]>("P2");
  const [board, setBoard] = useState(defaultBoard ?? "");
  const [registerProjectId, setRegisterProjectId] = useState(projects?.[0]?.id ?? "");
  const [boards, setBoards] = useState<JiraBoardRef[]>([]);
  const [users, setUsers] = useState<JiraAssignableUser[]>([]);
  const [busy, setBusy] = useState(false);
  /** Keep jira-only fields mounted briefly so width/opacity can animate out. */
  const [jiraFieldsVisible, setJiraFieldsVisible] = useState(kind === "jira");
  const morphTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const effectiveProjectId =
    scope === "board" ? boardProjectId : registerProjectId || boardProjectId || undefined;

  useEffect(() => {
    if (!enabled.includes(kind)) setKind(enabled[0] ?? "note");
  }, [enabled, kind]);

  useEffect(() => {
    if (defaultBoard) setBoard((prev) => prev || defaultBoard);
  }, [defaultBoard]);

  useEffect(() => {
    if (scope !== "register") return;
    if (projects?.length) {
      if (!registerProjectId || !projects.some((p) => p.id === registerProjectId)) {
        setRegisterProjectId(projects[0].id);
      }
      return;
    }
    if (boardProjectId) setRegisterProjectId(boardProjectId);
  }, [scope, projects, registerProjectId, boardProjectId]);

  useEffect(() => {
    if (scope !== "register" || !projects) return;
    const p = projects.find((x) => x.id === registerProjectId);
    if (p?.board) setBoard(p.board);
  }, [scope, projects, registerProjectId]);

  useEffect(() => {
    if (!enabled.includes("jira")) return;
    void listJiraBoards()
      .then((r) => setBoards(r.boards))
      .catch(() => setBoards([]));
    void listJiraAssignableUsers()
      .then(setUsers)
      .catch(() => setUsers([]));
  }, [enabled]);

  useEffect(() => {
    if (kind === "jira") {
      if (morphTimer.current) clearTimeout(morphTimer.current);
      setJiraFieldsVisible(true);
      return;
    }
    morphTimer.current = setTimeout(() => setJiraFieldsVisible(false), 120);
    return () => {
      if (morphTimer.current) clearTimeout(morphTimer.current);
    };
  }, [kind]);

  const selectedBoard = useMemo(
    () => boards.find((b) => b.key === board) ?? null,
    [boards, board]
  );

  const boardDisplay = selectedBoard
    ? boardLabel(selectedBoard)
    : board
      ? board
      : "Board";

  const placeholder = useMemo(() => {
    if (kind === "jira") {
      return `New issue on ${boardDisplay} — press Enter`;
    }
    if (kind === "question") return "Ask a question for the next person…";
    return "Note something for the next person…";
  }, [kind, boardDisplay]);

  const assigneeName = useMemo(() => {
    if (!assigneeId) return "";
    return users.find((u) => u.accountId === assigneeId)?.displayName ?? "";
  }, [assigneeId, users]);

  const canSubmit = Boolean(text.trim() && !busy && effectiveProjectId && (kind !== "question" || assigneeName));

  const commit = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || busy || !effectiveProjectId) return;
    if (kind === "question" && !assigneeName) {
      enqueueSnackbar("Pick who the question is for", { variant: "warning" });
      return;
    }
    setBusy(true);
    try {
      if (kind === "jira") {
        const issue = await projectJiraCreate(effectiveProjectId, {
          summary: trimmed,
          board: board || undefined,
          priority: PRI_TO_JIRA[priority],
          assigneeAccountId: assigneeId || undefined,
          initialStatus: "To Do",
        });
        const boardName = selectedBoard ? boardLabel(selectedBoard) : board || "board";
        enqueueSnackbar(`${issue.key} created on ${boardName}`, { variant: "success" });
        onCreated({
          kind: "jira",
          key: issue.key,
          url: issue.url,
          status: issue.status,
          summary: trimmed,
          assignee: assigneeName || null,
          priority: PRI_TO_JIRA[priority],
        });
      } else {
        const created = await createProjectAction({
          projectId: effectiveProjectId,
          kind,
          text: trimmed,
          ...(assigneeName ? { assignee: assigneeName } : {}),
        });
        onCreated({ kind, id: created.id, action: created });
      }
      setText("");
      inputRef.current?.focus();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Create failed", { variant: "error" });
    } finally {
      setBusy(false);
    }
  }, [
    text,
    busy,
    effectiveProjectId,
    kind,
    assigneeName,
    assigneeId,
    board,
    priority,
    selectedBoard,
    enqueueSnackbar,
    onCreated,
  ]);

  const onTypeChange = (next: WorkKind) => {
    setKind(next);
    // Keep focus in the text input across morph.
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const showJiraChrome = kind === "jira" || jiraFieldsVisible;
  const morphingOut = kind !== "jira" && jiraFieldsVisible;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "7px 8px 7px 12px",
          border: `1px solid ${c.border}`,
          borderRadius: `${brainTokens.radius.card}px`,
          background: c.pageBg,
          minHeight: 44,
        }}
      >
        <Box sx={{ color: c.faint, display: "inline-flex", flex: "none" }}>
          <Icon name="plus" size={16} />
        </Box>

        <InputBase
          inputRef={inputRef}
          value={text}
          disabled={busy}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void commit();
            }
          }}
          placeholder={placeholder}
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 13.5,
            color: c.ink2,
            fontFamily: brainTokens.font.sans,
            "& input": { padding: "5px 0" },
            "& input::placeholder": { color: c.placeholder, opacity: 1 },
          }}
        />

        <InlineField label="Type">
          <Select
            variant="standard"
            disableUnderline
            value={kind}
            disabled={busy}
            onChange={(e) => onTypeChange(e.target.value as WorkKind)}
            sx={fieldSelectSx}
          >
            {enabled.map((t) => (
              <MenuItem key={t} value={t}>
                {TYPE_LABEL[t]}
              </MenuItem>
            ))}
          </Select>
        </InlineField>

        {scope === "register" && projects && projects.length > 1 && (
          <InlineField label="Project">
            <Select
              variant="standard"
              disableUnderline
              value={registerProjectId}
              disabled={busy}
              onChange={(e) => setRegisterProjectId(String(e.target.value))}
              sx={fieldSelectSx}
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </InlineField>
        )}

        <InlineField label={kind === "jira" ? "Assignee" : "For"}>
          <Select
            variant="standard"
            disableUnderline
            displayEmpty
            value={assigneeId}
            disabled={busy}
            onChange={(e) => setAssigneeId(String(e.target.value))}
            renderValue={(v) => {
              if (!v) return kind === "jira" ? "Unassigned" : "—";
              return users.find((u) => u.accountId === v)?.displayName ?? "—";
            }}
            sx={fieldSelectSx}
          >
            <MenuItem value="">{kind === "jira" ? "Unassigned" : "—"}</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.accountId} value={u.accountId}>
                {u.displayName}
              </MenuItem>
            ))}
          </Select>
        </InlineField>

        {showJiraChrome && (
          <>
            <InlineField label="Priority" morphingOut={morphingOut}>
              <Select
                variant="standard"
                disableUnderline
                value={priority}
                disabled={busy || morphingOut}
                onChange={(e) => setPriority(e.target.value as (typeof PRI_OPTIONS)[number])}
                sx={fieldSelectSx}
              >
                {PRI_OPTIONS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </InlineField>

            <InlineField label="Board" morphingOut={morphingOut}>
              <Select
                variant="standard"
                disableUnderline
                displayEmpty
                value={board}
                disabled={busy || morphingOut}
                onChange={(e) => setBoard(String(e.target.value))}
                renderValue={(v) => {
                  if (!v) return "—";
                  const b = boards.find((x) => x.key === v);
                  return b ? boardLabel(b) : String(v);
                }}
                sx={fieldSelectSx}
              >
                {boards.map((b) => (
                  <MenuItem key={b.key} value={b.key}>
                    {boardLabel(b)}
                  </MenuItem>
                ))}
                {board && !boards.some((b) => b.key === board) && (
                  <MenuItem value={board}>{board}</MenuItem>
                )}
              </Select>
            </InlineField>
          </>
        )}

        <Box sx={{ width: "1px", height: 22, background: c.borderLight, flex: "none" }} />

        <Button
          size="small"
          variant="contained"
          disabled={!canSubmit}
          onClick={() => void commit()}
          sx={{ flex: "none", minWidth: 0, px: 1.5, py: 0.5, fontSize: 12.5 }}
        >
          {kind === "jira" ? "Create" : "Save"}
        </Button>
      </Box>
    </Box>
  );
}
